import moment from "moment-timezone";

/**
 * Centralized timezone conversion utilities for checkout delivery dates.
 *
 * The store backend operates in Kuwait timezone. Dates coming from the API
 * (e.g. `expected_delivery_time`) are in Kuwait time. When displaying dates
 * to the user we convert to their local timezone, and when submitting back
 * to the API we convert from the user's timezone to Kuwait time.
 */

/** IANA timezone used by the store backend */
export const STORE_TIMEZONE = "Asia/Kuwait";

/** Returns the user's IANA timezone string (e.g. "America/New_York") */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Parse a date string that is in the **store (Kuwait) timezone** and return a
 * moment instance converted to the **user's local timezone**.
 *
 * Use this before computing initial delivery dates so all day-of-week /
 * time-of-day comparisons happen in the user's clock.
 *
 * @param dateStr  – date/datetime string from the backend (Kuwait time)
 * @returns moment in the user's local timezone
 */
export const storeToUserTimezone = (dateStr: string) => {
  // Use explicit formats with "en" locale so English AM/PM is always
  // recognized, even when the global moment locale is Arabic.
  const parsed = moment.tz(
    dateStr,
    [
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD hh:mm:ss a",
      "YYYY-MM-DD hh:mm a",
      "YYYY-MM-DD",
    ],
    "en",
    STORE_TIMEZONE,
  );
  return {
    date: parsed.tz(getUserTimezone()),
    timezone: getUserTimezone(),
  };
};

/**
 * Convert a **user-local** Date / moment to the **store (Kuwait) timezone**.
 *
 * @param date – Date object or moment representing user-local time
 * @returns moment in Kuwait timezone
 */
export const userToStoreTimezone = (date: Date | moment.Moment | string) => {
  return { date: moment(date).tz(STORE_TIMEZONE), timezone: STORE_TIMEZONE };
};

/**
 * Convert user-local date and time slot to Kuwait-timezone date and time.
 *
 * Merges the date and time into a single timestamp in the user's timezone,
 * adds the 1-minute offset, converts to Kuwait timezone, and then splits
 * them back. This ensures that time conversions that cross midnight correctly
 * update the date (e.g. 11PM Cairo -> 12AM Kuwait next day).
 *
 * @param dateInput - Date object, moment, or string (e.g. "2026-02-19")
 * @param timeSlotInput - Time slot string (e.g. "14:00 - 15:00")
 */
export const convertUserToStoreDateTime = (
  dateInput: string | Date | moment.Moment,
  timeSlotInput: string,
  options: { addMinute?: boolean; timeFormat?: string } = {},
) => {
  const { addMinute = true, timeFormat = "HH:mm:ss" } = options;
  if (!dateInput && !timeSlotInput) {
    return {
      delivery_date: null,
      delivery_time: null,
      time_zone: STORE_TIMEZONE,
    };
  }

  // 1. Parse inputs into a single moment object in the USER's timezone
  const dateStr = moment(dateInput).format("YYYY-MM-DD");
  const timeStr = timeSlotInput ? timeSlotInput.split(" - ")[0] : "00:00"; // Default to start of day if no time

  const userDateTime = moment.tz(
    `${dateStr} ${timeStr}`,
    "YYYY-MM-DD HH:mm",
    getUserTimezone(),
  );

  // 2. Apply business logic: add 1 minute to the start of the slot
  if (timeSlotInput && addMinute) {
    userDateTime.add(1, "minute");
  }

  // 3. Convert to Store (Kuwait) Timezone
  const storeDateTime = userDateTime.clone().tz(STORE_TIMEZONE);

  return {
    delivery_date: dateInput
      ? storeDateTime.locale("en").format("YYYY-MM-DD")
      : null,
    delivery_time: timeSlotInput
      ? storeDateTime.locale("en").format(timeFormat)
      : null,
    time_zone: STORE_TIMEZONE,
  };
};

/**
 * Convert store (Kuwait) date and time slot to user-local date and time.
 *
 * Merges the date and time, converts to user timezone, and returns the
 * formatted strings.
 *
 * @param dateInput - Date string (e.g. "2026-02-24")
 * @param timeSlotInput - Time string (e.g. "02:26 pm" or "14:26")
 */
export const convertStoreToUserDateTime = (
  dateInput: string,
  timeSlotInput: string,
) => {
  if (!dateInput && !timeSlotInput) {
    return {
      date: null,
      time: null,
    };
  }

  // 1. Parse inputs into single moment object in STORE timezone
  // Handle "hh:mm a" (02:26 pm) and "HH:mm" (14:26) formats
  // Force "en" locale so English "am"/"pm" tokens are always recognized,
  // even when the global locale is Arabic.
  const storeDateTime = moment.tz(
    `${dateInput} ${timeSlotInput}`,
    ["YYYY-MM-DD hh:mm a", "YYYY-MM-DD HH:mm"],
    "en",
    STORE_TIMEZONE,
  );

  // 2. Convert to User Timezone
  const userDateTime = storeDateTime.clone().tz(getUserTimezone());

  return {
    date: userDateTime.locale("en").format("YYYY-MM-DD"),
    time: userDateTime.locale("en").format("hh:mm a"), // Keep "02:26 pm" format for display if used
    original_time_format: userDateTime.locale("en").format("HH:mm"),
  };
};

/**
 * Convert a timestamp (in milliseconds) from the backend to the user's local timezone.
 *
 * @param timestamp - Timestamp in milliseconds (representing Kuwait time)
 * @returns moment in the user's local timezone
 */
export const timestampToUserTimezone = (
  timestamp: number | string | undefined | null,
) => {
  if (!timestamp) return null;
  return moment(Number(timestamp)).tz(getUserTimezone());
};
